import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  first,
  firstValueFrom,
  map,
  tap,
  withLatestFrom,
} from "rxjs";
import { merge, pick, cloneDeep, mergeWith } from "lodash-es";

import { AppDataService } from "../data/app-data.service";
import { AnswerService } from "../answers/answers.service";
import { AlertService } from "../alerts/alerts.service";
import { Sample, SampleDataRanges, isSample } from "./sample";
import { IR_GRAPH, NMR_GRAPH } from "../../graph/graph-defaults.constant";
import { CompoundTree } from "../../shared/compound/compound-tree";

import { console } from "#/log";
import { Chem, Compounds, GraphType } from "#/declarations";
import { JsonDataService } from "#/app/shared/json-data.service";
import type { GraphData } from "./graph-data";
import { CompoundRender } from "#/app/shared/compound/compound-render";
import { UserBuild } from "../data/data.service";
import { GraphConfig } from "#/app/graph/graph-config";
import { AnalyticsService } from "../analytics/analytics.service";

/**
 * Sample Service
 * Handles all data related operations for a sample's ranges, notes, build,
 * and progress.
 * TODO: refactor so observables and not BehaviorSubjects are exposed, only
 *  this service should be able to call `.next()`.
 */

@Injectable()
export class SampleService {
  sample = new BehaviorSubject<Sample | undefined>(undefined);
  selectedIrRange: BehaviorSubject<any> = new BehaviorSubject(null);
  selectedNmrRange: BehaviorSubject<any> = new BehaviorSubject(null);
  currentRanges: Subject<SampleDataRanges> = new Subject();
  currentBuild: Observable<UserBuild> = EMPTY;

  complete: boolean = false;
  protected mostRecentProgress: number = -1;
  protected initFlags: any = { ranges: false, build: false };
  protected sampleData: Sample | null = null;

  constructor(
    private appData: AppDataService,
    private answerService: AnswerService,
    private alertService: AlertService,
    private jsonDataService: JsonDataService,
    protected analytics: AnalyticsService,
  ) {
    this.getProgressObservable().subscribe(
      (sampleProgress) => (this.mostRecentProgress = sampleProgress),
    );
  }

  public get answer() {
    this.assertSampleData();
    return this.answerService.getSolutionStructure(this.sampleData.$key);
  }

  // Progress related functions
  getProgress(): number {
    return this.mostRecentProgress;
  }

  getProgressObservable(): Observable<number> {
    return combineLatest([this.appData.getSamplesProgress(), this.sample]).pipe(
      map(([progress, sample]) => {
        if (sample && sample.$key in progress)
          return progress[sample.$key].progress;
        return 0;
      }),
    );
  }

  getSample() {
    return this.sample;
  }

  /**
   * Set the current value to the
   * sample passed.  Passing null clears the current sample.
   */
  setSample(sample: Sample | null) {
    console.log("sample.service: setting sample", sample);
    this.currentRanges?.complete();

    /*** if setting a non-null sample ***/
    if (sample) {
      /*** set this.sampleData either by lookup or by value ***/
      if (typeof sample === "string") {
        this.sampleData = this.appData.getSampleMetadata(sample);
      } else {
        this.sampleData = sample;
      }

      /**
       * Initialize sampleRanges from metadata; user data from
       */
      this.currentRanges = new BehaviorSubject<SampleDataRanges>(
        this.rangesFromSampleData(),
      );

      /*** Tell the data services about the current sample ***/
      this.appData.setCurrentSample(
        this.sampleData?.$key || this.sampleData?.id || null,
      );

      console.log(
        "sample.service: updating observables for build/ranges/notes ",
      );

      // this.appData
      //   .getCurrentBuild()
      //   .subscribe(this.onAppDataBuildUpdate.bind(this));

      this.updateCurrentBuildObservable();

      this.updateCurrentRangesSubscription();

      this.appData
        .getCurrentNotes()
        .subscribe(this.onAppDataNotesUpdate.bind(this));
      // this.onAppDataBuildUpdate(this.build);

      console.log(
        "sample.service: Finished setting subscriptions for build/ranges/notes",
      );

      /*** Notify this.sample observable subscribers ***/
      console.log("sample.service: updating sample");
      this.updateSample();
    } else {
      this.sampleData = null;
      this.mostRecentProgress = -1;
      this.currentRanges.complete();
      this.appData.setCurrentSample(this.sampleData);
      this.updateSample();
    }
  }

  /**
   * Set build-complete status in the data service
   */
  async setComplete(complete: boolean): Promise<void> {
    await this.appData.updateBuild({ complete: complete });

    if (complete && this.sampleData) {
      this.analytics.trackSampleComplete(this.sampleData.$key);
    }
  }

  /**
   * Set build in the data service
   */
  async setBuild(build: Compounds.Molecule): Promise<void> {
    console.trace(
      `sample.service: setBuild() for current sample ${this.sampleData?.$key}`,
      build,
    );
    await this.appData.updateBuild({ render: build });
  }

  /**
   * Set notes in the data service
   */
  async setNotes(notes: any): Promise<void> {
    await this.appData.setNotes(notes);
  }

  /**
   * Sets the selected IR or NMR range
   */
  setSelectedRange(peak: any) {
    if (peak.type === "ir") {
      this.selectedIrRange.next(peak);
    } else if (peak.type === "nmr") {
      this.selectedNmrRange.next(peak);
    }
  }

  /**
   * Clears the selected IR or NMR range
   */
  clearSelectedRange(type: string) {
    if (type === "ir") {
      this.selectedIrRange.next(null);
    } else if (type === "nmr") {
      this.selectedNmrRange.next(null);
    }
  }

  /**
   * Updates the range in the data service and updates the progress for
   * the sample.
   */
  async saveRange(rangeData: any): Promise<void> {
    const serializeProperties = [
      "assigned",
      "intensity",
      "shape",
      "hydrogens",
      "splitting",
      "neighbors",
      "correct",
    ];

    await this.appData.updateRange(
      rangeData.$key,
      pick(rangeData, serializeProperties),
    );
  }

  /**
   * Calculates the progress and calls the data service to store it.
   */
  async updateProgress(forceComplete = false): Promise<void> {
    if (this.initFlags.ranges && this.initFlags.build) {
      this.assertSampleData();
      const currentRanges = await firstValueFrom(this.currentRanges);

      let progress = forceComplete
        ? 100
        : this.calculateSampleProgress(currentRanges);
      if (progress > -1 && progress !== this.mostRecentProgress) {
        const started = performance.now();
        console.debug("sample.service: updating progress", progress);
        await this.appData.setSampleProgress(this.sampleData.$key, progress);
        console.debug(
          `sample.service: updated progress after ${performance.now() - started} ms`,
        );
      }
    }
  }

  /**
   * Gets the graph settings for the sample and provided graph type
   */
  getGraphConfig(type: GraphType): GraphConfig {
    let sample = this.sample.getValue();
    let config = type === "ir" ? IR_GRAPH : NMR_GRAPH;

    const configFromSample = sample?.[type]?.graph;
    if (configFromSample) {
      config = merge(config, configFromSample);
    }
    return config;
  }

  /**
   * Validates a peak and shows an alert to the user based on if it was
   * correct or incorrect.
   *
   * @returns List of incorrect fields
   *
   * @see {@link AnswerService.getIncorrectFields}
   */
  async validatePeakAnswer(peak: any): Promise<string[]> {
    console.debug("sample.service: validate answer", this.sampleData, peak);
    let incorrectFields = this.getIncorrectFields(peak);
    if (incorrectFields.length > 0) {
      await this.alertService.showAlert("incorrect_peak", incorrectFields);
    } else {
      await this.alertService.showAlert("correct_peak");
    }
    return incorrectFields;
  }

  /**
   * Validates a peak and returns a boolean if the answer is correct
   */
  isPeakAnswerCorrect(peak: any) {
    return this.getIncorrectFields(peak).length === 0;
  }

  /**
   * Validates the build and shows an alert informing the user if they were
   * correct or incorrect.
   */
  async showBuildResults(build: Compounds.Structure): Promise<boolean> {
    console.debug("sample.service: validating build", build, this.answer);

    if (this.sampleData !== null) {
      let incorrectKeys = this.answerService.validateBuild(build, this.answer);
      if (incorrectKeys.length === 0 || 1 === 1) {
        await this.alertService.showModalAlert("correct_build", [
          this.sampleData.compound,
          this.sampleData.name,
          this.sampleData.description,
          this.sampleData.$key,
        ]);
        return true;
      } else {
        await this.alertService.showAlert("incorrect_build", incorrectKeys);
        return false;
      }
    } else {
      // Handle the case where sampleData is null, if needed
      console.error("sampleData is null");
      return false; // or return something else
    }
  }

  /**
   * Removes a range from the build and resolved a promise with true or false
   * depending on if the user confirmed the remove is ok. If the range is not
   * in the build, it resolves true.
   */
  async removeRangeFromBuild(range: any): Promise<boolean> {
    const build = await this.currentBuildRenderForUpdate();

    if (CompoundTree.areRangeItemsInBuild(build, range)) {
      if (!this.complete) {
        const confirmed = await this.alertService.showConfirmAlert(
          "confirm_parent_remove",
        );
        if (confirmed) {
          let newBuild = CompoundTree.removeRangeFromBuild(build, range);
          this.analytics.trackBuild(
            this.sampleData?.$key,
            range.assigned?.$key,
            "remove",
            false,
            newBuild.structure === CompoundRender.EMPTY.structure,
          );

          this.setBuild(newBuild);
        }
        return confirmed;
      } else {
        await this.alertService.showAlert("build_already_complete");
        return false;
      }
    } else {
      return true;
    }
  }

  /**
   * Removes a group from the build that corresponds to the group id.
   */
  async removeGroupFromBuild(groupId: string): Promise<boolean> {
    const build = await this.currentBuildRenderForUpdate();
    const root = this.getGroupRoot(build, groupId);

    if (!root) {
      console.warn(`Group root not found for groupId: ${groupId}`, build);
      this.analytics.trackError(
        "remove-group-root-not-found",
        `group-id: ${groupId}`,
        false,
      );
      return false; // or handle it differently as needed
    }

    if (this.hasAttachedGroup(root)) {
      const confirmed = await this.alertService.showConfirmAlert(
        "confirm_parent_remove",
      );
      if (confirmed) {
        this.removeMoleculeFromBuild(build, root.moleculeId!);
        this.analytics.trackBuild(
          this.sampleData?.$key,
          groupId,
          "remove",
          true,
          build.structure === CompoundRender.EMPTY.structure,
        );
      }

      return confirmed;
    } else {
      this.removeMoleculeFromBuild(build, root.moleculeId!);
      this.analytics.trackBuild(
        this.sampleData?.$key,
        groupId,
        "remove",
        true,
        build.structure === CompoundRender.EMPTY.structure,
      );
      return true;
    }
  }

  /**
   * async getter for currentBuild's current value
   *
   * cloneDeep() so operations on the build data won't mutate
   * the object managed by the Observable
   */
  private async currentBuildRenderForUpdate(): Promise<Compounds.Molecule> {
    return firstValueFrom(
      this.currentBuild.pipe(
        first(),
        map(({ render }) => cloneDeep(render)),
      ),
    );
  }

  /**
   * Removes a molecule from the build, then saves.
   */
  async removeMoleculeFromBuild(build: Compounds.Molecule, moleculeId: string) {
    CompoundTree.removeMoleculeById(build, moleculeId);
    if (
      typeof build.structure !== "string" &&
      build.structure.atoms &&
      build.structure.atoms.length === 0
    ) {
      build.structure = CompoundRender.EMPTY.structure;
    }
    await this.setBuild(build);
  }

  /**
   * Checks if the selected peak is correct
   */
  isPeakCorrect(peak: any) {
    return !(this.getIncorrectFields(peak).indexOf("assigned") > -1);
  }

  /**
   * Checks if the build is correct
   */
  isBuildCorrect(build: Compounds.Structure): boolean {
    let incorrectKeys = this.answerService.validateBuild(build, this.answer);
    return incorrectKeys.length === 0;
  }

  /**
   * Checks if a given peak is a carboxylic acid peak (used for alternate
   * data display on these peaks per client)
   * This is a reliable test so long as any other carboxylic acid groups are
   * given ids according to the existing pattern
   */
  isCarboxylicAcidPeak(peak: { $key: any }): boolean {
    try {
      this.assertSampleData();
      let answer = this.answerService.getAnswer(
        this.sampleData.$key,
        peak.$key,
      );
      return answer.assigned.id.indexOf("carboxylic-acid") > -1;
    } catch (e) {
      return false;
    }
  }

  /** Assertion checking that this.sampleData is set and a valid sample */
  protected assertSampleData(): asserts this is typeof this & {
    sampleData: Sample;
  } {
    if (!isSample(this.sampleData))
      throw new Error("sample service: expected sampleData initialized");
  }

  /**
   * molecule has attached group if there is an assignment that doesn't
   * match the rangeId.
   * TODO: move to compound tree
   */
  private hasAttachedGroup(
    molecule: Compounds.Molecule,
    groupId?: string | null,
  ): boolean {
    if (!groupId) {
      groupId = molecule.groupId;
    }
    if (molecule.groupId !== groupId) {
      return true;
    }
    if (molecule.assign && molecule.assign.length > 0) {
      for (var i = 0; i < molecule.assign.length; i++) {
        if (this.hasAttachedGroup(molecule.assign[i].molecule, groupId)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Searches the compound tree for molecules with the provided groupId, then
   * finds the root molecule from the list of molecules in the group.
   * TODO: move to compound tree
   */
  getGroupRoot(
    build: Compounds.Molecule,
    groupId: string,
  ): Compounds.Molecule | null {
    let molecules = CompoundTree.getMoleculesByProperty(
      build,
      "groupId",
      groupId,
    );
    let baseMolecule = null;
    molecules.forEach((m) => {
      if (m.isBase) {
        baseMolecule = m;
      }
    });
    return baseMolecule;
  }

  /**
   * Gets the incorrect fields for a peak assignment.
   */
  private getIncorrectFields(peak: any): Array<string> {
    this.assertSampleData();
    return this.answerService.getIncorrectFields(this.sampleData.$key, peak);
  }

  /**
   * Updates the sample subscribers with new sample data
   */
  private updateSample() {
    this.sample.next(this.sampleData ? cloneDeep(this.sampleData) : undefined);
  }

  /**
   * Reinitialize this.currentBuild from data provider, and re-subscribe
   * for updating internal state
   */
  private updateCurrentBuildObservable() {
    this.currentBuild = this.appData.getCurrentBuild().pipe(
      tap((build) => {
        if (!build?.render)
          throw new Error(
            `sample.service currentBuild received invalid data: ${JSON.stringify(build)}`,
          );

        console.debug("sample.service: appData build updated", build);
      }),
    );

    /**
     * triggered anytime the build changes;
     * checking if the build is too big, and updating local data
     */
    this.currentBuild.subscribe((build) => {
      if (CompoundTree.getDepth(build.render) > 8) {
        this.alertService.showAlert("molecule_too_big");
      }
      this.complete = build.complete;
      this.initFlags.build = true;
    });
  }

  private updateCurrentRangesSubscription() {
    this.appData
      .getCurrentRanges()
      .pipe(
        tap((ranges) =>
          console.debug(
            `sample.service: appData currentRanges update:`,
            ranges,
          ),
        ),
        /** Include most recent update of our currentRanges (will be null on first update) */
        withLatestFrom(this.currentRanges),
        tap((bothRanges) =>
          console.debug(
            `sample.service: appData currentRanges withLatestFrom sampleRanges:`,
            bothRanges,
          ),
        ),
      )
      .subscribe(this.onAppDataRangesUpdate.bind(this));
  }

  /**
   * Merges supplied range item into (mutating) ir/nmr ranges on this.sampleData, matching by $key
   */
  private mergeRange(range: Chem.Range, sampleDataRanges: SampleDataRanges) {
    this.assertSampleData();
    if (sampleDataRanges.ir && sampleDataRanges.nmr) {
      // console.debug(
      //   ` -- -- merging range`,
      //   range,
      //   `into ${sampleDataRanges.ir.ranges.length + sampleDataRanges.nmr.ranges.length} ranges`,
      // );
      const ranges = [
        ...sampleDataRanges.ir.ranges,
        ...sampleDataRanges.nmr.ranges,
      ];
      for (let i = 0; i < ranges.length; i++) {
        if (range.$key === ranges[i].$key) {
          mergeWith(
            ranges[i],
            range,
            /** 'assigned' proton/group should be assigned literally, not recursively merged' */
            (_value, srcValue, key, _obj, _srcObj, stack) =>
              key === "assigned" && stack.size === 0 ? srcValue : undefined,
          );
        }
      }
    }
  }

  /**
   * Merges the user range data into sampleDataRanges ir/nmr ranges
   */
  private addRangesToSampleData(
    userRanges: Chem.Range[],
    sampleDataRanges: SampleDataRanges,
  ) {
    // console.debug(
    //   ` -- merging ${userRanges.length} ranges into ${(sampleDataRanges?.ir?.ranges.length ?? 0) + (sampleDataRanges?.nmr?.ranges?.length ?? 0)} ranges`,
    // );
    userRanges.forEach((range) => this.mergeRange(range, sampleDataRanges));
    return sampleDataRanges;
  }

  /**
   * - merge appData range data updates into sampleDataRanges ir/nmr ranges
   * - update this.currentRanges
   * - set range init flag
   */
  private onAppDataRangesUpdate([appDataRanges, oldSampleDataRanges]: [
    Chem.Range[],
    any,
  ]) {
    this.assertSampleData();
    // console.debug(
    //   `sample.service: got ${appDataRanges.length} appData ranges`,
    //   appDataRanges,
    //   !!appDataRanges.length,
    //   `; sampleData for '${this.sampleData.$key}'  has ${this.sampleData.ir?.ranges.length} IR and ${this.sampleData.nmr?.ranges.length} NMR ranges`,
    // );
    if (appDataRanges) {
      const newRangesValue = this.addRangesToSampleData(
        appDataRanges,
        oldSampleDataRanges,
      );
      // console.debug(
      //   `sample.service: after merging ranges, sampleData for '${this.sampleData.$key}' has ${this.sampleData.ir?.ranges.length} IR and ${this.sampleData.nmr?.ranges.length} NMR ranges; updating sampleRanges with`,
      //   newRangesValue,
      // );

      this.currentRanges.next(newRangesValue);
      this.initFlags.ranges = true;
    }
  }

  protected rangesFromSampleData(): SampleDataRanges {
    if (!this.sampleData)
      throw new Error(
        `No current sample while picking ranges from sample data.`,
      );
    const ranges = pick(this.sampleData, ["ir", "nmr"]) as Pick<
      Sample,
      "ir" | "nmr"
    >;

    if (!(ranges.ir && ranges.nmr)) {
      throw new Error(
        `Sample ${this.sampleData.$key ?? this.sampleData.id} has incomplete range data: "${JSON.stringify(ranges)}"`,
      );
    }
    return cloneDeep(ranges) as SampleDataRanges;
  }

  /**
   * Updates the notes and sample anytime they change.
   */
  private onAppDataNotesUpdate(notes: any) {
    console.debug("sample.service: notes subscribe", notes);

    if (this.sampleData) {
      this.sampleData.notes = "$value" in notes ? notes.$value : notes;

      this.updateSample();
    } else {
      console.warn("sampleData is null; cannot assign notes.");
    }
  }

  /**
   * helper to get progress for ranges
   */
  private calculateRangeProgress(ranges: any[]) {
    if (ranges.length === 0) {
      return 0;
    }
    let assignedRanges = 0;
    ranges.forEach((d: { assigned: any }) => {
      if (d.assigned && this.isPeakAnswerCorrect(d)) {
        assignedRanges++;
      }
    });
    return (assignedRanges / ranges.length) * 100;
  }

  /**
   * Calculates the progress on the sample passed to it.
   * (IR: 33%, NMR: 33%, Build: 34%)
   * TODO: should only add to progress if the assignment is right
   */
  private calculateSampleProgress(currentRanges?: SampleDataRanges): number {
    let progress = 0;

    // check build
    if (this.complete) {
      return 100;
    }

    if (currentRanges?.ir && currentRanges?.nmr) {
      // check peaks
      let irPeaks = currentRanges.ir;
      if (irPeaks && irPeaks.ranges) {
        progress += this.calculateRangeProgress(irPeaks.ranges) * 0.33;
      }
      let nmrPeaks = currentRanges.nmr;
      if (nmrPeaks && nmrPeaks.ranges) {
        progress += this.calculateRangeProgress(nmrPeaks.ranges) * 0.33;
      }
      return Math.round(progress);
    }
    // the data to calculate progress is not available
    return -1;
  }

  /**
   * Fetch graph datasets for current sample.
   *
   * @param graphType Select graph data to load ("nmr" or "ir")
   * @returns Observable for graph data
   */
  public getCurrentDataset(graphType: GraphType): Observable<GraphData[]> {
    this.assertSampleData();
    return this.jsonDataService.loadGraphData(this.sampleData.$key, graphType);
  }
}
